import React, { useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import Navbar from './Navbar'
import { useI18n } from '../lib/i18n'

interface HeroDesktopProps {
  backgroundImageUrl?: string;
  backgroundVideoUrl?: string;
  logoUrl?: string;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
}

const bgImageVariants = {
  hidden: { scale: 1.1, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 1.5, ease: "easeOut" }
  }
}

const headlineVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.8, ease: "easeOut" }
  }
}

const subtitleVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.8, delay: 0.2, ease: "easeOut" }
  }
}

const cardVariants = {
  hidden: { y: 40, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.8, delay: 0.4, ease: "easeOut" }
  }
}

const badgeVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.5, delay: 0.8, ease: "backOut" }
  }
}

export default function HeroDesktop({
  backgroundImageUrl = "/images/hero-desktop.png",
  backgroundVideoUrl
}: HeroDesktopProps) {
  const containerRef = useRef<HTMLElement>(null)
  const navigate = useNavigate()
  const { t } = useI18n()
  const [arrivalDate, setArrivalDate] = useState('')
  const [departureDate, setDepartureDate] = useState('')

  const handleSubmit = () => {
    if (arrivalDate && departureDate) {
      navigate(`/buchen?arrivalDate=${arrivalDate}&departureDate=${departureDate}&step=2`)
    } else {
      navigate('/buchen')
    }
  }

  return (
    <motion.section 
      ref={containerRef}
      className="hero relative min-h-screen w-full overflow-hidden bg-[#F4F5F7]"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Background Image */}
      <motion.img 
        src={backgroundImageUrl} 
        alt="" 
        crossOrigin="anonymous" 
        className="absolute inset-0 w-full h-full object-cover" 
        variants={bgImageVariants}
      />

      {/* Navbar */}
      <Navbar />

      {/* Left Content: Headline & Subtitle */}
      <motion.h1 
        className="headline absolute left-[8vw] top-[22vh] text-[5.2vh] font-bold text-[#172B4D] leading-[1.15] tracking-tight"
        variants={headlineVariants}
      >
        {t.hero.headline}<br />
        {t.hero.headlineLine2}
      </motion.h1>

      <motion.p
        className="subtitle absolute left-[8vw] top-[36vh] text-[2vh] text-[#42526E] leading-relaxed max-w-[38vw]"
        variants={subtitleVariants}
      >
        {t.hero.subtitle}<br />
        {t.hero.subtitleLine2}
      </motion.p>

      {/* Booking Widget Card */}
      <motion.div
        className="absolute left-[8vw] top-[48vh] w-[36vw] bg-white rounded-2xl shadow-xl p-[2vw] z-10"
        variants={cardVariants}
      >
        {/* Date Inputs Row */}
        <div className="flex gap-[1.5vw] mb-[2.5vh]">
          {/* Hinfahrt */}
          <div className="flex-1">
            <label className="block text-[1.4vh] font-medium text-[#172B4D] mb-[0.8vh]">{t.hero.arrivalLabel}</label>
            <input
              type="date"
              value={arrivalDate}
              onChange={(e) => setArrivalDate(e.target.value)}
              min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
              className="w-full border border-gray-200 rounded-lg px-[0.8vw] py-[1.2vh] text-[1.6vh] text-[#172B4D] focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] transition-all"
            />
            <p className="text-[1.1vh] text-gray-500 mt-[0.5vh]">{t.hero.bookingNote}</p>
          </div>
          {/* Rückfahrt */}
          <div className="flex-1">
            <label className="block text-[1.4vh] font-medium text-[#172B4D] mb-[0.8vh]">{t.hero.departureLabel}</label>
            <input
              type="date"
              value={departureDate}
              onChange={(e) => setDepartureDate(e.target.value)}
              min={arrivalDate || new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
              className="w-full border border-gray-200 rounded-lg px-[0.8vw] py-[1.2vh] text-[1.6vh] text-[#172B4D] focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] transition-all"
            />
          </div>
        </div>

        {/* Submit Button */}
        <motion.button
          onClick={handleSubmit}
          className="cta-button w-full bg-[#0052CC] text-white font-bold text-[1.8vh] py-[1.5vh] rounded-full shadow-md"
          whileHover={{
            scale: 1.03,
            backgroundColor: '#0747A6',
            boxShadow: '0px 10px 20px rgba(0, 82, 204, 0.3)'
          }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.2 }}
        >
          {t.hero.ctaButton}
        </motion.button>
      </motion.div>

          </motion.section>
  )
}